import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IoMdAdd } from "react-icons/io";
import { TbLogout } from "react-icons/tb"; 
import { HiGlobeAmericas } from "react-icons/hi2";  
import { IoMdPerson } from "react-icons/io";
import { useAuth } from '../AuthContext';
import { userService, wenziService } from '../services/api';
import { FaPlug, FaEdit, FaTrash } from "react-icons/fa";
import { FaUserFriends} from "react-icons/fa"
import ConnectModal from './ConnectModal';
import MatchesModal from './MatchesModal';
import { Link } from 'react-router-dom';

const Profile = () => {
  const MAX_WENZIS = 3;
  const navigate = useNavigate();
  const { user, logout, login } = useAuth();
  const [wenzis, setWenzis] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newWenzi, setNewWenzi] = useState({ 
    name: '', 
    gender: ''
  });
  const [deleteConfirmation, setDeleteConfirmation] = useState({ isOpen: false, wenziId: null, wenziName: '' });
  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isMatchesModalOpen, setIsMatchesModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingWenzi, setEditingWenzi] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [showMaxWenziPopup, setShowMaxWenziPopup] = useState(false);
  const [isVerificationModalOpen, setIsVerificationModalOpen] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationStatus, setVerificationStatus] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [tempPhoneNumber, setTempPhoneNumber] = useState('');
  const [featureFlags, setFeatureFlags] = useState(null);
  const [isCreationTypeModalOpen, setIsCreationTypeModalOpen] = useState(false);
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [groupPhoneNumbers, setGroupPhoneNumbers] = useState(['']);
  const [phoneValidation, setPhoneValidation] = useState([]);
  const [userGroupCount, setUserGroupCount] = useState(0);

  const baseUrl = process.env.REACT_APP_API_URL;


  useEffect(() => {
    if (!user) {
      navigate('/login');
    } else {
      fetchWenzis();
      fetchFeatureFlags();
      fetchUserGroupCount();
      
      // Only refresh user data once on initial mount, not on user changes
      // This prevents continuous polling
    }
  }, [user, navigate]);

  const fetchUserGroupCount = async () => {
    try {
      const response = await userService.getUserGroupMembershipsCount(user.id);
      setUserGroupCount(response.data.group_count);
    } catch (error) {
      console.error('Failed to fetch user group count:', error);
    }
  };

  const fetchFeatureFlags = async () => {
    try{
      const response = await fetch(`${baseUrl}/api/feature-flags`, {
        method: "GET"
      });
      const data = await response.json();
      setFeatureFlags(data);
    } catch (error) {
      console.error('Failed to fetch feature flags:', error);
    }
  }

  const handleSubscription = () => {
    navigate('/Subscription')
  }
  
  // One-time refresh on component mount only to get latest user data
  useEffect(() => {
    if (user) {
      const refreshUserData = async () => {
        try {
          const response = await userService.getUser(user.id);
          login(response.data); // Update user data in context
        } catch (error) {
          console.error('Failed to refresh user data:', error);
        }
      };
      
      refreshUserData();
    }
  }, []); // Empty dependency array means this runs once on mount

  const fetchWenzis = async () => {
    try {
      const response = await wenziService.getWenzis(user.id);
      setWenzis(response.data);
    } catch (error) {
      console.error('Failed to fetch wenzis:', error);
    }
  };

  const validatePhoneNumber = async (phoneNumber, index) => {
    if (!phoneNumber.trim()) {
      const newValidation = [...phoneValidation];
      newValidation[index] = { isValid: false, message: '' };
      setPhoneValidation(newValidation);
      return;
    }

    try {
      const response = await userService.validatePhoneForGroup(phoneNumber);
      const newValidation = [...phoneValidation];
      newValidation[index] = {
        isValid: response.data.isValid,
        message: response.data.message || '',
        userId: response.data.userId
      };
      setPhoneValidation(newValidation);
    } catch (error) {
      const newValidation = [...phoneValidation];
      newValidation[index] = { isValid: false, message: 'Error validating phone number' };
      setPhoneValidation(newValidation);
    }
  };

  // Get max number of wenzis based on subscription status
  const getMaxWenzis = () => {
    return user.subscription_status === "active" ? MAX_WENZIS : 1;
  };

const handleCreateNew = () => {
  // Check if the user's phone is verified
  if (!user.phone_verified) {
    // Open verification modal if phone is not verified
    setIsVerificationModalOpen(true);
    return;
  }
  
  const userMaxWenzis = getMaxWenzis();
  if (wenzis.length < userMaxWenzis) {
    setIsCreationTypeModalOpen(true);
  } else {
    setShowMaxWenziPopup(true);
    setTimeout(() => setShowMaxWenziPopup(false), 5000);
  }
};

const handleCreateOneOnOne = () => {
  setIsCreationTypeModalOpen(false);
  setIsModalOpen(true);
};

const handleCreateGroup = () => {
  setIsCreationTypeModalOpen(false);
  setGroupPhoneNumbers(['']);
  setPhoneValidation([]);
  setIsGroupModalOpen(true);
};

const addPhoneNumberField = () => {
  if (groupPhoneNumbers.length < 5) {
    setGroupPhoneNumbers([...groupPhoneNumbers, '']);
    setPhoneValidation([...phoneValidation, { isValid: false, message: '' }]);
  }
};

const removePhoneNumberField = (index) => {
  if (groupPhoneNumbers.length > 1) {
    const newNumbers = groupPhoneNumbers.filter((_, i) => i !== index);
    const newValidation = phoneValidation.filter((_, i) => i !== index);
    setGroupPhoneNumbers(newNumbers);
    setPhoneValidation(newValidation);
  }
};

const updatePhoneNumber = (index, value) => {
  const newNumbers = [...groupPhoneNumbers];
  newNumbers[index] = value;
  setGroupPhoneNumbers(newNumbers);
  
  // Validate after a short delay to avoid too many API calls
  clearTimeout(window.phoneValidationTimeout);
  window.phoneValidationTimeout = setTimeout(() => {
    validatePhoneNumber(value, index);
  }, 500);
};

const handleSendVerificationCode = async () => {
  try {
    setIsVerifying(true);
    setVerificationStatus('sending');
    
    
    const response = await userService.sendVerificationCode(user.phone_number);
    
    if (response.data && response.data.status) {
      setVerificationStatus('sent');
    } else {
      setVerificationStatus('error');
    }
  } catch (error) {
    console.error('Failed to send verification code:', error);
    let errorMessage = 'An error occurred while sending the code.';
    
    if (error.response && error.response.data && error.response.data.detail) {
      errorMessage = error.response.data.detail;
    }
    
    alert(`Error: ${errorMessage}`);
    setVerificationStatus('error');
  } finally {
    setIsVerifying(false);
  }
};

const handleVerifyCode = async () => {
  try {
    setIsVerifying(true);
    setVerificationStatus('verifying');
    
    
    const response = await userService.verifyPhoneNumber(user.phone_number, verificationCode);
    
    if (response.data.status === 'approved') {
      setVerificationStatus('success');
      
      // Update user data with verified status
      const updatedUser = {
        ...user,
        phone_verified: true
      };
      
      // Update user in context
      login(updatedUser);
      
      // Close modal after delay
      setTimeout(() => {
        setIsVerificationModalOpen(false);
        setVerificationCode('');
      }, 2000);
    } else {
      setVerificationStatus('invalid');
    }
  } catch (error) {
    console.error('Failed to verify code:', error);
    let errorMessage = 'An error occurred while verifying the code.';
    
    if (error.response && error.response.data && error.response.data.detail) {
      errorMessage = error.response.data.detail;
    }
    
    alert(`Error: ${errorMessage}`);
    setVerificationStatus('error');
  } finally {
    setIsVerifying(false);
  }
};

const handleUpdatePhoneNumber = async () => {
  try {
    setIsVerifying(true);
    
    if (!tempPhoneNumber || tempPhoneNumber.trim() === '') {
      alert('Please enter a valid phone number');
      setIsVerifying(false);
      return;
    }
    
    // Call the update user endpoint
    const response = await userService.updateUser(user.id, {
      phone_number: tempPhoneNumber,
      phone_verified: false // Reset verification status
    });
    
    // Update the user in context
    login(response.data);
    
    // Reset UI
    setIsEditingPhone(false);
    setTempPhoneNumber('');
    setVerificationStatus('');
    
    alert('Phone number updated. You will need to verify this number.');
  } catch (error) {
    console.error('Failed to update phone number:', error);
    let errorMessage = 'An error occurred while updating your phone number.';
    
    if (error.response && error.response.data && error.response.data.detail) {
      errorMessage = error.response.data.detail;
    }
    
    alert(`Error: ${errorMessage}`);
  } finally {
    setIsVerifying(false);
  }
};

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewWenzi({ name: '', gender: '' });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewWenzi(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitNewWenzi = async (e) => {
    e.preventDefault();
    try {
      // Only send name and gender
      await wenziService.createWenzi(user.id, newWenzi);
      await fetchWenzis();
      handleCloseModal();
      setIsPolling(true);
    } catch (error) {
      console.error('Failed to create wenzi:', error);
      // Show max wenzi limit error or other errors
      if (error.message && error.message.includes('maximum limit')) {
        setShowMaxWenziPopup(true);
        setTimeout(() => setShowMaxWenziPopup(false), 5000);
        handleCloseModal();
      } else {
        alert(`Error: ${error.message || 'Failed to create Wenzi. Please try again.'}`);
      }
    }
  };

  const handleGroupSubmit = async (e) => {
    e.preventDefault();
    
    // Check if we have at least one valid phone number
    const validPhoneNumbers = groupPhoneNumbers.filter((phone, index) => 
      phone.trim() && phoneValidation[index]?.isValid
    );
    
    if (validPhoneNumbers.length === 0) {
      alert('All numbers must be verified');
      return;
    }
    
    try {
      const groupData = {
        name: newWenzi.name,
        gender: newWenzi.gender,
        phone_numbers: validPhoneNumbers, // Send phone numbers instead of user IDs
        creator_id: user.id // Include the current user as the creator
      };
      
      await wenziService.createGroupWenzi(groupData);
      await fetchWenzis();
      await fetchUserGroupCount(); // Update group count after creating a group
      setIsGroupModalOpen(false);
      setGroupPhoneNumbers(['']);
      setPhoneValidation([]);
      setNewWenzi({ name: '', gender: '' });
      setIsPolling(true);
    } catch (error) {
      console.error('Failed to create group wenzi:', error);
      alert(`Error: ${error.message || 'Failed to create Group Wenzi. Please try again.'}`);
    }
  };

  useEffect(() => {
    let pollInterval;
    let pollCount = 0;
    const MAX_POLLS = 10; // Limit number of polls to avoid excessive requests
    
    if (isPolling) {
      pollInterval = setInterval(async () => {
        try {
          pollCount++;
          
          // Check if we've reached the maximum number of polls
          if (pollCount >= MAX_POLLS) {
            setIsPolling(false);
            return;
          }
          
          const response = await wenziService.getWenzis(user.id);
          const updatedWenzis = response.data;
          
          const hasUpdates = updatedWenzis.some((wenzi, index) => 
            wenzi.avatar !== wenzis[index]?.avatar
          );

          if (hasUpdates) {
            setWenzis(updatedWenzis);
            setIsPolling(false);
          }
        } catch (error) {
          console.error('Error during polling:', error);
          setIsPolling(false);
        }
      }, 3000); // Increased to 3 seconds to reduce load
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [isPolling, user.id, wenzis]);

  const handleDeleteWenzi = (id, name) => {
    setDeleteConfirmation({ isOpen: true, wenziId: id, wenziName: name });
  };

  const confirmDelete = async () => {
    try {
      await wenziService.deleteWenzi(deleteConfirmation.wenziId);
      fetchWenzis();
    } catch (error) {
      console.error('Failed to delete wenzi:', error);
    }
    setDeleteConfirmation({ isOpen: false, wenziId: null, wenziName: '' });
  };

  const cancelDelete = () => {
    setDeleteConfirmation({ isOpen: false, wenziId: null, wenziName: '' });
  };

  const handleEditProfile = () => {
    navigate('/edit-profile');
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleConnectClick = () => {
    setIsConnectModalOpen(true);
  };

  const handleConnectUpdate = (updatedUser) => {
    login(updatedUser); // Update the user in the global state
    setIsConnectModalOpen(false);
  };

  const handleCloseConnectModal = () => {
    setIsConnectModalOpen(false);
  };

  const handleMatchesClick = () => {
    setIsMatchesModalOpen(true);
  }

  const handleMatchesUpdate = (updatedUser) => {
    login(updatedUser);
    setIsMatchesModalOpen(false);
  }

  const handleCloseMatchesModal = () => {
    setIsMatchesModalOpen(false);
  };

  const handleEditWenzi = (wenzi) => {
    setEditingWenzi(wenzi);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingWenzi(null);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await wenziService.updateWenzi(editingWenzi.id, editingWenzi);
      await fetchWenzis();
      handleCloseEditModal();
    } catch (error) {
      console.error('Failed to update wenzi:', error);
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingWenzi(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="flex flex-col min-h-screen w-full bg-cover bg-center bg-no-repeat" style={{ backgroundImage: "url('/images/leelo_landing.jpg')" }}>
      <div className="w-full max-w-7xl mx-auto p-5 flex flex-col items-center justify-center">
      <header className="flex justify-end items-center mb-5 px-5 w-full">
        <div className="flex items-center gap-7">
          <span className="flex items-center gap-2 text-white">
            {user.city}, {user.state} <HiGlobeAmericas className="text-lg" />
          </span>
          <button 
            onClick={handleEditProfile} 
            className="flex items-center gap-1 font-bold text-white hover:text-tropical-indigo cursor-pointer flex-row-reverse"
          >
            <IoMdPerson className="text-lg" /> Edit Profile
          </button>
          {featureFlags && featureFlags.leelo_plus && (
            <button onClick={handleSubscription} className='flex items-center gap-1 font-bold text-white hover:text-tropical-indigo cursor-pointer flex-row-reverse'>
            Subscribe
            </button>
          )}
        </div>
      </header>
      
      <main className="flex-grow bg-gradient-to-br from-white/95 to-uranian-blue/80 backdrop-blur-sm shadow-xl rounded-lg py-24 px-16 w-full max-w-12xl mx-auto border border-white/20">
        {/* Desktop header */}
        <div className="hidden sm:flex justify-between items-center mb-10">
          <h1 className="text-3xl font-bold text-tropical-indigo">Welcome, {user.first_name}!</h1>
          <div className="flex gap-3">
            <button
              onClick={handleCreateNew}
              disabled={wenzis.length >= getMaxWenzis()}
              className={`flex items-center justify-center gap-2 ${
                !user.phone_verified 
                ? 'bg-yellow-100 text-yellow-800 border border-yellow-500' 
                : 'bg-white text-black border border-black hover:text-tropical-indigo hover:border-tropical-indigo'
              } rounded px-5 py-3 text-base ${
                wenzis.length >= getMaxWenzis() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <span>{!user.phone_verified ? 'Verify Phone' : 'Create New'}</span>
              {user.phone_verified ? <IoMdAdd className="text-lg" /> : null}
            </button>
            <button 
              onClick={user.phone_verified ? handleConnectClick : () => setIsVerificationModalOpen(true)} 
              className={`flex items-center justify-center gap-2 ${
                user.phone_verified 
                  ? 'bg-tropical-indigo text-white border border-tropical-indigo hover:opacity-90' 
                  : 'bg-tropical-indigo/30 text-white/70 border border-tropical-indigo/30 cursor-not-allowed'
              } rounded px-5 py-3 text-base`}
              title={!user.phone_verified ? "Verify phone first" : ""}
            >
              <FaPlug />
              <span>Join Matching</span>
            </button>
            <button 
              onClick={user.phone_verified ? handleMatchesClick : () => setIsVerificationModalOpen(true)} 
              className={`flex items-center justify-center gap-2 ${
                user.phone_verified 
                  ? 'bg-jordy-blue text-white border border-jordy-blue hover:opacity-90' 
                  : 'bg-jordy-blue/30 text-white/70 border border-jordy-blue/30 cursor-not-allowed'
              } rounded px-5 py-3 text-base`}
              title={!user.phone_verified ? "Verify phone first" : ""}
            >
              <FaUserFriends />
              <span>My Matches</span>
            </button>
          </div>
        </div>
        
        {/* Mobile header */}
        <div className="sm:hidden mb-10">
          <h1 className="text-2xl font-bold mb-6 text-center text-tropical-indigo">Welcome, {user.first_name}!</h1>
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            <button
              onClick={handleCreateNew}
              disabled={wenzis.length >= getMaxWenzis()}
              className={`flex items-center justify-center gap-1 ${
                !user.phone_verified 
                ? 'bg-yellow-100 text-yellow-800 border border-yellow-500' 
                : 'bg-white text-black border border-black'
              } rounded px-4 py-1.5 text-sm ${
                wenzis.length >= getMaxWenzis() ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              }`}
            >
              <span>{!user.phone_verified ? 'Verify Phone' : 'Create New'}</span>
              {user.phone_verified ? <IoMdAdd /> : null}
            </button>
            <button 
              onClick={user.phone_verified ? handleConnectClick : () => setIsVerificationModalOpen(true)} 
              className={`flex items-center justify-center gap-1 ${
                user.phone_verified 
                  ? 'bg-tropical-indigo text-white border border-tropical-indigo' 
                  : 'bg-tropical-indigo/30 text-white/70 border border-tropical-indigo/30 cursor-not-allowed'
              } rounded px-3 py-1.5 text-sm`}
              title={!user.phone_verified ? "Verify phone first" : ""}
            >
              <FaPlug className="text-xs" />
              <span>Join Matching</span>
            </button>
            <button 
              onClick={user.phone_verified ? handleMatchesClick : () => setIsVerificationModalOpen(true)} 
              className={`flex items-center justify-center gap-1 ${
                user.phone_verified 
                  ? 'bg-jordy-blue text-white border border-jordy-blue' 
                  : 'bg-jordy-blue/30 text-white/70 border border-jordy-blue/30 cursor-not-allowed'
              } rounded px-4 py-1.5 text-sm`}
              title={!user.phone_verified ? "Verify phone first" : ""}
            >
              <FaUserFriends className="text-xs" />
              <span>My Matches</span>
            </button>
          </div>
        </div>
        
        {/* Wenzi cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mt-20">
          {wenzis.map(wenzi => (
            <div key={wenzi.id} className="border border-white/40 bg-white/70 shadow-md rounded-lg p-10 text-center hover:shadow-lg transition-shadow">
              <div className="w-full">
                <h3 className="text-2xl font-bold mb-3 text-tropical-indigo">{wenzi.name}</h3>
                <div className="flex justify-center mb-4">
                  <div className="inline-flex items-center">
                    <span className="text-gray-600 text-base font-medium">{wenzi.phone_number}</span>
                    <button 
                      onClick={() => {
                        navigator.clipboard.writeText(wenzi.phone_number);
                        alert("Copied to clipboard!");
                      }}
                      className="ml-2 text-gray-500 hover:text-tropical-indigo flex items-center"
                      title="Copy to clipboard"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <rect x="6" y="4" width="10" height="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
                        <rect x="4" y="6" width="10" height="10" stroke="currentColor" strokeWidth="1.5" fill="none" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="bg-blue-50/80 border border-blue-100/80 rounded-md p-2 mb-3 text-sm text-blue-900 font-bold">
                  {wenzi.is_group ? (
                    <p> Check your phone and enjoy your group chat with {wenzi.name}!</p>
                  ) : (
                    <p>Add {wenzi.name} to your contacts and start texting!</p>
                  )}
                </div>
                <div className="flex justify-center gap-4">
                  <button 
                    onClick={() => handleEditWenzi(wenzi)} 
                    className="bg-tropical-indigo text-white border border-tropical-indigo rounded-full w-10 h-10 hover:opacity-90 flex items-center justify-center" 
                    title="Edit"
                  >
                    <FaEdit />
                  </button>
                  <button 
                    onClick={() => handleDeleteWenzi(wenzi.id, wenzi.name)} 
                    className="bg-white text-black border border-black rounded-full w-10 h-10 hover:text-red-500 hover:border-red-500 flex items-center justify-center"
                    title="Delete"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </main>
      
      {/* Footer */}
      <footer className="flex flex-col sm:flex-row justify-between items-center py-8 px-6 w-full mt-6">
        <p className="text-white text-xs mb-3 sm:mb-0">
          2025 © Wenzi, Inc. 
          <span className="mx-2">•</span> 
          <Link to="/privacy" className="text-white hover:underline">Privacy Policy</Link>
          <span className="mx-2">•</span> 
          <a href="https://discord.gg/z859AyVU3R" target="_blank" rel="noopener noreferrer" className="text-white hover:underline">Join our Discord</a>
        </p>
        <button 
          onClick={handleLogout} 
          className="bg-transparent border-none cursor-pointer font-bold text-white hover:text-tropical-indigo flex items-center gap-1 flex-row-reverse"
        >
          <TbLogout className="text-lg" /> Log Out
        </button>
      </footer>
      

      {/* Create Wenzi Modal */}
      {isModalOpen && wenzis.length < getMaxWenzis() && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 max-w-md text-center">
            <h2 className="text-xl font-bold mb-4">Create Your AI Thought Partner</h2>
            <form onSubmit={handleSubmitNewWenzi} className="flex flex-col">
              <input
                type="text"
                name="name"
                value={newWenzi.name}
                onChange={handleInputChange}
                placeholder="Name"
                required
                className="w-full p-3 border border-gray-300 rounded mb-4"
              />
              <select
                name="gender"
                value={newWenzi.gender}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded mb-6 bg-white"
              >
                <option value="">Select Gender</option>
                <option value="Man">Man</option>
                <option value="Woman">Woman</option>
              </select>
              <div className="flex justify-center gap-4">
                <button 
                  type="button" 
                  onClick={handleCloseModal}
                  className="px-6 py-2 bg-white border border-black rounded hover:text-vinous hover:border-vinous font-bold"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-tropical-indigo text-white border-none rounded hover:opacity-90 font-bold"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 max-w-md text-center">
            <h2 className="text-xl font-bold mb-4">Confirm Deletion</h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete <span className="font-semibold">{deleteConfirmation.wenziName}</span>?
            </p>
            <div className="flex justify-center gap-4">
              <button 
                type="button" 
                onClick={cancelDelete}
                className="px-6 py-2 bg-white border border-black rounded hover:text-vinous hover:border-vinous font-bold"
              >
                Cancel
              </button>
              <button 
                type="button" 
                onClick={confirmDelete}
                className="px-6 py-2 bg-red-600 text-white border-none rounded hover:opacity-90 font-bold"
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connect Modal */}
      {isConnectModalOpen && (
        <ConnectModal
          user={user}
          onClose={handleCloseConnectModal}
          onUpdate={handleConnectUpdate}
        />
      )}

      {/* Matches Modal */}
      {isMatchesModalOpen && (
        <MatchesModal
          user={user}
          onClose={handleCloseMatchesModal}
          onUpdate={handleMatchesUpdate}
        />
      )}

      {/* Edit Wenzi Modal */}
      {isEditModalOpen && editingWenzi && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 max-w-md text-center">
            <h2 className="text-xl font-bold mb-4">Edit Name</h2>
            <form onSubmit={handleEditSubmit} className="flex flex-col">
              <input
                type="text"
                name="name"
                value={editingWenzi.name}
                onChange={handleEditInputChange}
                placeholder="Name"
                required
                className="w-full p-3 border border-gray-300 rounded mb-6"
              />
              <div className="flex justify-center gap-4">
                <button 
                  type="button" 
                  onClick={handleCloseEditModal}
                  className="px-6 py-2 bg-white border border-black rounded hover:text-vinous hover:border-vinous font-bold"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="px-6 py-2 bg-tropical-indigo text-white border-none rounded hover:opacity-90 font-bold"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Max Wenzi Popup */}
      {showMaxWenziPopup && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white py-3 px-6 rounded shadow-lg z-50 text-center">
          {user.subscription_status === "active" 
            ? `You can't make more than ${MAX_WENZIS} AI thought partners` 
            : `Free users can only create 1 AI thought partner. Upgrade to premium for up to ${MAX_WENZIS} partners.`}
        </div>
      )}

      {/* Phone Verification Modal */}
      {isVerificationModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-8 rounded-lg w-11/12 max-w-md">
            <h2 className="text-xl font-bold mb-4 text-center">Verify Your Phone Number</h2>
            <p className="text-gray-600 mb-6 text-center">
              We need to verify your phone number before you can create a thought partner.
            </p>

            {verificationStatus !== 'sent' && verificationStatus !== 'verifying' && verificationStatus !== 'success' && !isEditingPhone && (
              <div className="mb-6 text-center">
                <p className="text-gray-700 mb-4">We'll send a 6-digit code to:</p>
                <p className="text-lg font-bold mb-4">{user.phone_number}</p>
                <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded mb-4 text-sm">
                  <p>Make sure your phone number is in a valid format.</p>
                  <p className="mt-1">Use international format: +1XXXXXXXXXX</p>
                </div>
                <div className="flex flex-col gap-2">
                  <button 
                    onClick={handleSendVerificationCode}
                    disabled={isVerifying}
                    className="px-5 py-3 bg-tropical-indigo text-white rounded hover:opacity-90 w-full font-bold"
                  >
                    {isVerifying ? 'Sending...' : 'Send Verification Code'}
                  </button>
                  <button 
                    onClick={() => {
                      setIsEditingPhone(true);
                      setTempPhoneNumber(user.phone_number);
                    }}
                    className="px-5 py-2 text-tropical-indigo border border-tropical-indigo rounded hover:bg-tropical-indigo/5"
                  >
                    Update Phone Number
                  </button>
                </div>
                {verificationStatus === 'error' && (
                  <p className="text-red-600 mt-2">Failed to send code. Please try again or update your phone number.</p>
                )}
              </div>
            )}
            
            {isEditingPhone && (
              <div className="mb-6">
                <p className="text-gray-700 mb-4 text-center">Update your phone number:</p>
                <input
                  type="tel"
                  value={tempPhoneNumber}
                  onChange={(e) => setTempPhoneNumber(e.target.value)}
                  placeholder="Enter phone number with country code"
                  className="w-full p-3 border border-gray-300 rounded mb-4 text-center"
                />
                <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded mb-4 text-sm">
                  <p>Enter phone number in international format: +1XXXXXXXXXX</p>
                  <p className="mt-1">You will need to verify this number after updating.</p>
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={() => setIsEditingPhone(false)}
                    className="px-5 py-3 bg-white border border-gray-300 rounded hover:bg-gray-50 flex-1"
                  >
                    Cancel
                  </button>
                  <button 
                    onClick={handleUpdatePhoneNumber}
                    disabled={isVerifying}
                    className="px-5 py-3 bg-tropical-indigo text-white rounded hover:opacity-90 flex-1 font-bold"
                  >
                    {isVerifying ? 'Updating...' : 'Update Phone'}
                  </button>
                </div>
              </div>
            )}

            {(verificationStatus === 'sent' || verificationStatus === 'verifying' || verificationStatus === 'invalid') && (
              <div className="mb-6">
                <p className="text-gray-700 mb-4 text-center">Enter the 6-digit code sent to your phone:</p>
                <p className="text-sm text-green-700 mb-4 text-center">
                  A verification code has been sent to {user.phone_number}
                </p>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  maxLength={6}
                  className="w-full p-3 border border-gray-300 rounded mb-4 text-center text-lg font-mono"
                />
                <button 
                  onClick={handleVerifyCode}
                  disabled={verificationCode.length !== 6 || isVerifying}
                  className={`px-5 py-3 bg-tropical-indigo text-white rounded hover:opacity-90 w-full font-bold ${
                    verificationCode.length !== 6 ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isVerifying ? 'Verifying...' : 'Verify Code'}
                </button>
                {verificationStatus === 'invalid' && (
                  <p className="text-red-600 mt-2 text-center">Invalid code. Please try again.</p>
                )}
                <p className="text-sm text-gray-600 mt-4 text-center">
                  Didn't receive the code? <button 
                    onClick={handleSendVerificationCode} 
                    className="text-tropical-indigo"
                    disabled={isVerifying}
                  >
                    Resend code
                  </button>
                </p>
              </div>
            )}

            {verificationStatus === 'success' && (
              <div className="mb-6 text-center">
                <p className="text-green-600 font-bold mb-4">Phone number verified successfully!</p>
                <p className="text-gray-700">You are good to go!</p>
              </div>
            )}

            <div className="flex justify-center">
              <button 
                onClick={() => {
                  setIsVerificationModalOpen(false);
                  setVerificationCode('');
                  setVerificationStatus('');
                }}
                className="px-5 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 font-bold"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Creation Type Selection Modal */}
      {isCreationTypeModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 max-w-md text-center">
            <h2 className="text-xl font-bold mb-4">Create New AI Thought Partner</h2>
            <p className="text-gray-600 mb-6">Choose the type of AI thought partner you'd like to create:</p>
            <div className="flex flex-col gap-4">
              <button 
                onClick={handleCreateOneOnOne}
                className="px-6 py-3 bg-tropical-indigo text-white rounded hover:opacity-90 font-bold"
              >
                One-on-One
              </button>
              <button 
                onClick={handleCreateGroup}
                disabled={userGroupCount >= 2}
                className={`px-6 py-3 text-white rounded font-bold ${
                  userGroupCount >= 2 
                    ? 'bg-gray-400 cursor-not-allowed' 
                    : 'bg-jordy-blue hover:opacity-90'
                }`}
                title={userGroupCount >= 2 ? 'You can only be in 2 groups maximum' : ''}
              >
                Group Chat
              </button>
            </div>
            <div className="flex justify-center mt-6">
              <button 
                onClick={() => setIsCreationTypeModalOpen(false)}
                className="px-6 py-2 bg-white border border-gray-300 rounded hover:bg-gray-50 font-bold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Creation Modal */}
      {isGroupModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-11/12 max-w-lg">
            <h2 className="text-xl font-bold mb-4 text-center">Create Group AI Thought Partner</h2>
            <form onSubmit={handleGroupSubmit}>
              <input
                type="text"
                name="name"
                placeholder="AI Thought Partner Name"
                value={newWenzi.name}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded mb-4"
              />
              <select
                name="gender"
                value={newWenzi.gender}
                onChange={handleInputChange}
                required
                className="w-full p-3 border border-gray-300 rounded mb-4 bg-white"
              >
                <option value="">Select Gender</option>
                <option value="Man">Man</option>
                <option value="Woman">Woman</option>
              </select>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Add Group Members by Phone Number:
                </label>
                <div className="space-y-2">
                  {groupPhoneNumbers.map((phoneNumber, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => updatePhoneNumber(index, e.target.value)}
                        placeholder="Enter phone number (e.g., +1234567890)"
                        className={`flex-1 p-2 border rounded ${
                          phoneValidation[index]?.isValid === false && phoneNumber.trim() 
                            ? 'border-red-500' 
                            : phoneValidation[index]?.isValid 
                            ? 'border-green-500' 
                            : 'border-gray-300'
                        }`}
                      />
                      <div className="flex items-center gap-1">
                        {phoneNumber.trim() && phoneValidation[index] && (
                          <span className={`text-sm ${
                            phoneValidation[index].isValid ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {phoneValidation[index].isValid ? '✓' : '✗'}
                          </span>
                        )}
                        {groupPhoneNumbers.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removePhoneNumberField(index)}
                            className="text-red-500 hover:text-red-700 p-1"
                          >
                            ✗
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {groupPhoneNumbers.length < 5 ? (
                  <button
                    type="button"
                    onClick={addPhoneNumberField}
                    className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                  >
                    + Add another phone number
                  </button>
                ) : (
                  <p className="mt-2 text-gray-500 text-sm">
                    Maximum 5 members per group (6 including AI)
                  </p>
                )}
                {groupPhoneNumbers.filter((phone, index) => 
                  phone.trim() && phoneValidation[index]?.isValid
                ).length === 0 && (
                  <p className="text-red-500 text-sm mt-1">All numbers must be verified</p>
                )}
              </div>
              
              <div className="flex justify-center gap-4">
                <button 
                  type="button" 
                  onClick={() => {
                    setIsGroupModalOpen(false);
                    setGroupPhoneNumbers(['']);
                    setPhoneValidation([]);
                    setNewWenzi({ name: '', gender: '' });
                  }}
                  className="px-6 py-2 bg-white border border-black rounded hover:text-vinous hover:border-vinous font-bold"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={groupPhoneNumbers.filter((phone, index) => 
                    phone.trim() && phoneValidation[index]?.isValid
                  ).length === 0}
                  className={`px-6 py-2 bg-tropical-indigo text-white border-none rounded font-bold ${
                    groupPhoneNumbers.filter((phone, index) => 
                      phone.trim() && phoneValidation[index]?.isValid
                    ).length === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-90'
                  }`}
                >
                  Create Group
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default Profile;
